import { useEffect, useMemo, useState } from 'react'
import {
  BadgeCheck,
  Banknote,
  CircleDollarSign,
  Mail,
  Send,
  TicketCheck,
  Users,
  Undo2,
} from 'lucide-react'
import {
  collection,
  doc,
  getDoc,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore'
import { db } from '@/lib/firebase'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  Select,
  Spinner,
  SearchField,
  SegmentedControl,
  StatTile,
} from '@/components/ui'
import { Modal } from '@/components/Modal'
import { RegisterSlipsModal } from '@/components/RegisterSlipsModal'
import { useFeatures } from '@/lib/features'
import { useAuth } from '@/lib/auth'
import { useBooklets } from '@/hooks/useBooklets'
import { useQuerySnapshot } from '@/hooks/useQuerySnapshot'
import { useToast } from '@/components/Toast'
import { QueryError } from '@/components/QueryError'
import { InviteCard } from './InviteCard'
import { OrdersSection } from './OrdersSection'
import { registerDigitalSale } from '@/lib/api'
import { authErrorMessage } from '@/lib/authErrors'
import { formatCode } from '@shared/codes'
import {
  COLLECTIONS,
  CODE_STATUS_LABELS,
  hasAtLeastRole,
  type Code,
  type Team,
} from '@shared/model'
import { formatSek, normalizeForSearch } from '@/lib/format'
import { cn } from '@/lib/cn'

type CodeFilter = 'unpaid' | 'paid' | 'unsold' | 'all'

const FILTER_LABELS: Record<CodeFilter, string> = {
  unpaid: 'Obetalda',
  paid: 'Betalda',
  unsold: 'Osålda',
  all: 'Alla',
}

export function TeamPage() {
  const { user, teamId, role } = useAuth()
  const toast = useToast()
  const { booklets, activeBooklet, loading: bookletsLoading } = useBooklets()

  const [team, setTeam] = useState<Team | null>(null)
  const [bookletId, setBookletId] = useState<string>('')
  const [pickedTeamId, setPickedTeamId] = useState<string>('')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [filter, setFilter] = useState<CodeFilter>('unpaid')
  /** Senaste massavbockningen, så att ett felklick går att ta tillbaka. */
  const [lastBatch, setLastBatch] = useState<string[] | null>(null)
  const [saleOpen, setSaleOpen] = useState(false)
  const [slipsOpen, setSlipsOpen] = useState(false)
  const { features } = useFeatures()
  const [busy, setBusy] = useState(false)

  /**
   * Administratörer har inget eget lag men behöver kunna titta i vilket som
   * helst – dels för att hjälpa en lagledare, dels för att kunna prova
   * flödet utan att skapa ett andra konto.
   */
  const canPickTeam = hasAtLeastRole(role, 'admin')
  const teamsQuery = useMemo(
    () => (canPickTeam ? query(collection(db, COLLECTIONS.teams), orderBy('name')) : null),
    [canPickTeam],
  )
  const { data: teams } = useQuerySnapshot<Team>(teamsQuery)

  const effectiveTeamId = pickedTeamId || teamId || teams[0]?.id || ''

  /**
   * Välj period.
   *
   * Faller tillbaka i flera steg med flit: `activeBooklet` utesluter
   * testhäften så att ett provhäfte aldrig kapar köparvyn – men här inne
   * är det ofta precis testhäftet man vill titta på, och finns inget annat
   * skulle väljaren annars stå tom. Tom väljare gav dessutom ett lömskt
   * beteende: webbläsaren visar första alternativet trots att inget är valt,
   * så sidan såg ut att ha en period men frågade aldrig efter några koder.
   *
   * Kontrollen mot `booklets.some` gör dessutom att en vald period som
   * försvinner – t.ex. när testdatan rensas – ersätts i stället för att
   * lämna vyn tom.
   */
  useEffect(() => {
    if (!booklets.length) return
    if (bookletId && booklets.some((item) => item.id === bookletId)) return

    const fallback =
      activeBooklet ?? booklets.find((item) => item.status === 'active') ?? booklets[0]
    setBookletId(fallback.id)
  }, [booklets, activeBooklet, bookletId])

  useEffect(() => {
    if (!effectiveTeamId) return
    void getDoc(doc(db, COLLECTIONS.teams, effectiveTeamId)).then((snapshot) => {
      setTeam(snapshot.exists() ? ({ id: snapshot.id, ...snapshot.data() } as Team) : null)
    })
  }, [effectiveTeamId])

  const codesQuery = useMemo(() => {
    if (!effectiveTeamId || !bookletId) return null
    return query(
      collection(db, COLLECTIONS.codes),
      where('teamId', '==', effectiveTeamId),
      where('bookletId', '==', bookletId),
      orderBy('createdAt'),
    )
  }, [effectiveTeamId, bookletId])

  const { data: codes, loading: codesLoading, error: codesError } = useQuerySnapshot<Code>(codesQuery)

  const booklet = booklets.find((item) => item.id === bookletId) ?? null

  const stats = useMemo(() => {
    const sold = codes.filter((code) => code.status !== 'unsold').length
    const activated = codes.filter((code) => code.status === 'activated').length
    const paid = codes.filter((code) => code.paid).length
    return {
      total: codes.length,
      sold,
      activated,
      paid,
      unpaid: sold - paid,
      revenue: paid * (booklet?.priceSek ?? 0),
      outstanding: (sold - paid) * (booklet?.priceSek ?? 0),
    }
  }, [codes, booklet])

  /*
   * Obetalda är förvalt.
   *
   * Hela sidans syfte är avstämningen, och den handlar bara om koder som är
   * sålda men inte betalda. Att öppna på hela listan innebar att lagledaren
   * varje gång fick börja med att leta rätt på just dem.
   */
  const visible = useMemo(() => {
    const needle = normalizeForSearch(search)
    return codes.filter((code) => {
      if (filter === 'unpaid' && (code.paid || code.status === 'unsold')) return false
      if (filter === 'paid' && !code.paid) return false
      if (filter === 'unsold' && code.status !== 'unsold') return false
      if (!needle) return true
      return normalizeForSearch(
        `${formatCode(code.id)} ${code.soldToEmail ?? ''} ${code.soldByPlayerName ?? ''}`,
      ).includes(needle)
    })
  }, [codes, search, filter])

  /** De rader i den synliga listan som faktiskt går att bocka av. */
  const selectable = useMemo(
    () => visible.filter((code) => !code.paid && code.status !== 'unsold'),
    [visible],
  )

  async function togglePaid(code: Code, paid: boolean) {
    if (!user) return
    try {
      await updateDoc(doc(db, COLLECTIONS.codes, code.id), {
        paid,
        paidAt: paid ? serverTimestamp() : null,
        paidBy: user.uid,
      })
    } catch (caught) {
      toast.error(authErrorMessage(caught))
    }
  }

  /** Bockar av flera koder på en gång efter avstämning mot Digitala Lagkassan. */
  async function markSelectedPaid() {
    if (!user || selected.size === 0) return
    setBusy(true)
    const ids = [...selected]
    try {
      const batch = writeBatch(db)
      for (const codeId of ids) {
        batch.update(doc(db, COLLECTIONS.codes, codeId), {
          paid: true,
          paidAt: serverTimestamp(),
          paidBy: user.uid,
        })
      }
      await batch.commit()
      toast.success(`${ids.length} koder markerade som betalda.`)
      setSelected(new Set())
      setLastBatch(ids)
    } catch (caught) {
      toast.error(authErrorMessage(caught))
    } finally {
      setBusy(false)
    }
  }

  /*
   * Ångra hela den senaste avbockningen.
   *
   * Massavbockning är det enda stället i lagvyn där ett felklick träffar
   * många rader samtidigt – markerar man fel filter och trycker "välj alla"
   * är tjugo koder plötsligt betalda. Att leta rätt på dem en och en efteråt
   * är precis det arbete vyn finns för att slippa.
   */
  async function undoLastBatch() {
    if (!user || !lastBatch?.length) return
    setBusy(true)
    try {
      const batch = writeBatch(db)
      for (const codeId of lastBatch) {
        batch.update(doc(db, COLLECTIONS.codes, codeId), {
          paid: false,
          paidAt: null,
          paidBy: user.uid,
        })
      }
      await batch.commit()
      toast.success(`${lastBatch.length} koder är tillbaka som obetalda.`)
      setLastBatch(null)
    } catch (caught) {
      toast.error(authErrorMessage(caught))
    } finally {
      setBusy(false)
    }
  }

  if (!effectiveTeamId) {
    return (
      <EmptyState
        icon={<Users size={44} aria-hidden />}
        title={
          canPickTeam ? 'Inga lag upplagda än' : 'Inget lag kopplat till ditt konto'
        }
        description={
          canPickTeam
            ? 'Lägg upp lagen under Admin → Lag, så syns de här.'
            : 'Be en administratör koppla ditt konto till rätt lag.'
        }
      />
    )
  }

  if (bookletsLoading) return <Spinner />

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold">{team?.name ?? 'Mitt lag'}</h1>
          <p className="text-sm text-muted">
            Sålda häften och avstämning mot Digitala Lagkassan.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {canPickTeam && teams.length > 0 && (
            <Select
              value={effectiveTeamId}
              onChange={(event) => setPickedTeamId(event.target.value)}
              aria-label="Välj lag"
              className="w-auto min-w-44"
            >
              {teams.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </Select>
          )}

          <Select
            value={bookletId}
            onChange={(event) => setBookletId(event.target.value)}
            aria-label="Välj period"
            className="w-auto min-w-52"
          >
            {booklets.map((item) => (
              <option key={item.id} value={item.id}>
                {item.periodName}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* Nyckeltalen lagledaren faktiskt behöver: hur mycket som sålts och
          hur mycket som fortfarande inte kommit in i kassan. */}
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
        <StatTile label="Tilldelade" value={String(stats.total)} />
        <StatTile label="Sålda" value={`${stats.sold} av ${stats.total}`} tone="brand" />
        <StatTile label="Betalda" value={`${stats.paid} av ${stats.sold}`} tone="success" />
        <StatTile
          label="Utestående"
          value={formatSek(stats.outstanding)}
          tone={stats.outstanding > 0 ? 'warning' : 'neutral'}
        />
      </div>

      {features.digitalOrders && (
        <>
          <InviteCard teamId={effectiveTeamId} teamName={team?.name ?? 'ditt lag'} />
          <OrdersSection teamId={effectiveTeamId} bookletId={bookletId} />
        </>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {features.slipRegistration && (
          <Button icon={<TicketCheck size={17} />} onClick={() => setSlipsOpen(true)}>
            Registrera lappar åt förälder
          </Button>
        )}
        {features.digitalSale && (
          <Button variant="outline" icon={<Send size={17} />} onClick={() => setSaleOpen(true)}>
            Registrera digital försäljning
          </Button>
        )}
        {selectable.length > 0 && (
          <Button
            variant="quiet"
            onClick={() =>
              setSelected((current) =>
                current.size === selectable.length
                  ? new Set()
                  : new Set(selectable.map((code) => code.id)),
              )
            }
          >
            {selected.size === selectable.length ? 'Avmarkera alla' : `Välj alla ${selectable.length}`}
          </Button>
        )}
        {selected.size > 0 && (
          <Button
            variant="accent"
            icon={<BadgeCheck size={17} />}
            loading={busy}
            onClick={() => void markSelectedPaid()}
          >
            {/* Beloppet står i knappen med flit. Det är summan som ska stämma
                mot Digitala Lagkassan, inte antalet rader. */}
            Markera {selected.size} som betalda · {formatSek(selected.size * (booklet?.priceSek ?? 0))}
          </Button>
        )}
      </div>

      {lastBatch && lastBatch.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-border bg-surface-2 px-4 py-3">
          <p className="text-sm">
            <strong>{lastBatch.length} koder</strong> markerades som betalda.
          </p>
          <Button
            size="sm"
            variant="quiet"
            icon={<Undo2 size={15} />}
            loading={busy}
            onClick={() => void undoLastBatch()}
          >
            Ångra
          </Button>
        </div>
      )}

      <SegmentedControl<CodeFilter>
        label="Filtrera koderna"
        value={filter}
        onChange={(next) => {
          setFilter(next)
          // Markeringar hör till den lista man såg när man gjorde dem.
          setSelected(new Set())
        }}
        options={(['unpaid', 'paid', 'unsold', 'all'] as CodeFilter[]).map((value) => ({
          value,
          label: `${FILTER_LABELS[value]} (${
            value === 'unpaid'
              ? codes.filter((code) => !code.paid && code.status !== 'unsold').length
              : value === 'paid'
                ? codes.filter((code) => code.paid).length
                : value === 'unsold'
                  ? codes.filter((code) => code.status === 'unsold').length
                  : codes.length
          })`,
        }))}
      />

      <SearchField
        value={search}
        onChange={setSearch}
        placeholder="Sök kod, köparmail eller spelare"
        label="Sök bland lagets koder"
      />

      {codesError ? (
        <QueryError error={codesError} />
      ) : codesLoading ? (
        <Spinner />
      ) : visible.length === 0 ? (
        <EmptyState
          icon={<CircleDollarSign size={44} aria-hidden />}
          title="Inga koder än"
          description="När administratören genererat en kodbatch till laget dyker koderna upp här."
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <ul className="divide-y divide-border">
            {visible.map((code) => (
              /*
               * Ett radläge, inte två.
               *
               * Raden var tidigare flex-wrap med fyra syskon, vilket på en
               * telefon bröt statusbrickan och betald-rutan ned på var sin
               * egen rad i oförutsägbar ordning. Nu ligger koden och brickan
               * ihop som en enhet, detaljerna under, och betald-rutan alltid
               * längst till höger – samma uppställning oavsett bredd.
               */
              <li
                key={code.id}
                className={cn(
                  'flex items-start gap-3 p-3',
                  selected.has(code.id) && 'bg-brand-soft',
                )}
              >
                <input
                  type="checkbox"
                  checked={selected.has(code.id)}
                  disabled={code.paid || code.status === 'unsold'}
                  onChange={(event) => {
                    setSelected((current) => {
                      const next = new Set(current)
                      if (event.target.checked) next.add(code.id)
                      else next.delete(code.id)
                      return next
                    })
                  }}
                  aria-label={`Välj ${formatCode(code.id)}`}
                  className="size-5 shrink-0 accent-[var(--c-brand)] disabled:opacity-30"
                />

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="font-mono text-sm font-bold tracking-widest">
                      {formatCode(code.id)}
                    </span>
                    <Badge
                      tone={
                        code.status === 'activated'
                          ? 'success'
                          : code.status === 'sold'
                            ? 'brand'
                            : 'neutral'
                      }
                    >
                      {CODE_STATUS_LABELS[code.status]}
                    </Badge>
                  </div>
                  <p className="truncate text-xs text-muted">{codeHolderLine(code)}</p>
                </div>

                {/* Ordet "Betald" göms på de smalaste skärmarna men läses
                    fortfarande upp av skärmläsare – rutans färg och placering
                    räcker för ögat när utrymmet är slut. */}
                <label className="flex shrink-0 items-center gap-2 text-sm font-semibold">
                  <input
                    type="checkbox"
                    checked={code.paid}
                    onChange={(event) => void togglePaid(code, event.target.checked)}
                    aria-label={`Markera ${formatCode(code.id)} som betald`}
                    className="size-5 accent-[var(--c-success)]"
                  />
                  <span className={cn('hidden sm:inline', code.paid ? 'text-success' : 'text-muted')}>
                    <Banknote size={15} className="mr-1 -mt-0.5 inline" aria-hidden />
                    Betald
                  </span>
                  <Banknote
                    size={16}
                    aria-hidden
                    className={cn('sm:hidden', code.paid ? 'text-success' : 'text-muted')}
                  />
                </label>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <DigitalSaleModal
        open={saleOpen}
        onClose={() => setSaleOpen(false)}
        teamId={effectiveTeamId}
        bookletId={bookletId}
        bookletName={booklet?.periodName ?? ''}
        priceSek={booklet?.priceSek ?? 0}
        availableCount={codes.filter((code) => code.status === 'unsold').length}
      />

      {slipsOpen && (
        <RegisterSlipsModal mode="onBehalf" onClose={() => setSlipsOpen(false)} />
      )}
    </div>
  )
}

function DigitalSaleModal({
  open,
  onClose,
  teamId,
  bookletId,
  bookletName,
  priceSek,
  availableCount,
}: {
  open: boolean
  onClose: () => void
  teamId: string
  bookletId: string
  bookletName: string
  priceSek: number
  availableCount: number
}) {
  const toast = useToast()
  const [email, setEmail] = useState('')
  const [playerName, setPlayerName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function handleSubmit() {
    setError(null)
    if (!email.includes('@')) {
      setError('Fyll i köparens e-postadress.')
      return
    }
    setBusy(true)
    try {
      const result = await registerDigitalSale({
        teamId,
        bookletId,
        buyerEmail: email.trim(),
        playerName: playerName.trim() || undefined,
      })
      toast.success(
        result.emailSent
          ? `Aktiveringsmail skickat till ${email.trim()}.`
          : `Koden ${formatCode(result.codeId)} är reserverad till ${email.trim()}.`,
      )
      setEmail('')
      setPlayerName('')
      onClose()
    } catch (caught) {
      setError(authErrorMessage(caught))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Registrera digital försäljning"
      size="sm"
      footer={
        <div className="flex gap-2">
          <Button variant="quiet" fullWidth onClick={onClose}>
            Avbryt
          </Button>
          <Button
            fullWidth
            loading={busy}
            disabled={availableCount === 0}
            onClick={() => void handleSubmit()}
            icon={<Mail size={17} />}
          >
            Skicka
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}

        <p className="text-sm text-muted">
          Köparen får ett mail med en länk där hen sätter lösenord och får {bookletName} direkt.
          Betalningen sker som vanligt via Swish till Digitala Lagkassan
          {priceSek > 0 && <> ({formatSek(priceSek)})</>}.
        </p>

        {availableCount === 0 ? (
          <ErrorNote>
            Laget har inga osålda koder kvar för perioden. Be en administratör generera fler.
          </ErrorNote>
        ) : (
          <p className="text-sm font-semibold text-muted">
            {availableCount} koder kvar att sälja.
          </p>
        )}

        <Field label="Köparens e-post" required>
          {({ id }) => (
            <Input
              id={id}
              type="email"
              inputMode="email"
              autoCapitalize="none"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="namn@exempel.se"
            />
          )}
        </Field>

        <Field
          label="Vilken spelare sålde? (frivilligt)"
          hint="Fyll i om ni vill kunna se försäljning per spelare."
        >
          {({ id }) => (
            <Input
              id={id}
              value={playerName}
              onChange={(event) => setPlayerName(event.target.value)}
              placeholder="Förnamn Efternamn"
            />
          )}
        </Field>
      </div>
    </Modal>
  )
}

/**
 * Vem som håller lappen, i klartext.
 *
 * "Såld av" stod tidigare på varje lapp som hade ett spelarnamn, men det är
 * fel så snart lapparna registreras: en registrerad lapp ligger hos en
 * förälder som ska sälja den, inte hos någon som redan gjort det. För
 * lagledaren är det skillnaden mellan "utdelad" och "klar", och det är den
 * enda frågan hen har när hen läser listan.
 */
function codeHolderLine(code: Code): string {
  if (code.soldToEmail) return code.soldToEmail

  const who = code.soldByPlayerName ? ` · ${code.soldByPlayerName}` : ''

  if (code.status === 'activated') return `Aktiverad av köparen${who}`
  if (code.handedOutAt) return `Utdelad till köpare${who}`
  if (code.holderUid) return `Registrerad hos förälder${who}`
  return 'Inte utdelad'
}
